import { Button, InlineStack, Link, Tooltip } from '@customerio/pluma-components/react';
import { NO_EVENTS_TOOLTIP } from './webhook-state-field';

type WebhookSaveControlsProps = {
  /** No signing key yet, so this webhook is still being created. */
  isNewWebhook: boolean;
  isSaveDisabled: boolean;
  /** The webhook has no events selected, so it cannot be saved in an enabled state. */
  isForceDisabled: boolean;
  isSaving: boolean;
  cancelHref: string;
  onSaveAndEnable: () => void;
};

/**
 * The footer actions.
 *
 * Creating offers the choice between saving the webhook dormant and switching it on immediately;
 * editing has a single button, because the state toggle above already carries that decision.
 *
 * The unremarkable save on each flow is the form's submit button, so pressing Enter in a field
 * does the same thing as clicking it. Only "Save and Enable Webhook" is wired separately, because
 * it asks for a state the form is not otherwise in.
 */
export function WebhookSaveControls({
  isNewWebhook,
  isSaveDisabled,
  isForceDisabled,
  isSaving,
  cancelHref,
  onSaveAndEnable,
}: WebhookSaveControlsProps) {
  return (
    <InlineStack alignBlock="center" alignInline="end">
      {/*
        Held shut while the save is in flight, so the form cannot be navigated away from mid-write
        and surprise the user with a completion they have already left behind. Ember means to do
        this too but passes `disabled` as a bare attribute rather than Pluma's `isDisabled`, which
        only greys the link — it still navigates. This is the behaviour that markup was reaching for.
      */}
      <Link href={cancelHref} variant="secondary" mr="150" isDisabled={isSaving} data-test-webhook-form-cancel>
        Cancel
      </Link>

      {isNewWebhook ? (
        <>
          <Button
            type="submit"
            data-test-webhook-form-save
            variant="secondary"
            mr="150"
            isDisabled={isSaveDisabled}
            isLoading={isSaving}
          >
            Save
          </Button>
          <Tooltip isDisabled={!isForceDisabled} content={NO_EVENTS_TOOLTIP} isCustom>
            {(context) => (
              <Button
                ref={context.reference.setReference}
                {...context.reference.getReferenceProps()}
                data-test-webhook-form-save-enable
                variant="primary"
                isDisabled={isSaveDisabled || isForceDisabled}
                isLoading={isSaving}
                onClick={onSaveAndEnable}
              >
                Save and Enable Webhook
              </Button>
            )}
          </Tooltip>
        </>
      ) : (
        <Button
          type="submit"
          data-test-webhook-form-save
          variant="primary"
          isDisabled={isSaveDisabled}
          isLoading={isSaving}
        >
          Save Webhook
        </Button>
      )}
    </InlineStack>
  );
}
